import { resolvePlainWalkScope, reachReactionKey, WATCHER_REACH_ENTRY_TRIGGERS, WATCHER_REACH_LEAVE_TRIGGERS } from '../janus-engine/movement/plain-walk-scope.js';
import type { PlainWalkCommitInput, PlainWalkResult } from '../janus-engine/movement/plain-walk.js';
import type { MovementTruthRow } from '../janus-engine/movement/client-truth-commit.js';
import { awaitPlan } from '../janus-engine/actions/deferred-plan.js';
import { PC_DYING_STATUS_IDS } from '../janus-engine/actions/ability-use.js';
import { legacyStepPositions } from '../janus-engine/zones/walk-zone-crossings.js';
import { encounterCreatureField, posCoord } from '../shared/array-types.js';
import { withAsyncDeadline } from '../shared/async-deadline.js';
import { hydrateInventoryDefinitions } from '../janus-engine/inventory/definition.js';
import { readLayerListEntryUuid } from '../janus-engine/compile/inventory-raw-readers.js';
import { ordinaryMovementStartRefusal } from '../janus-engine/movement/movement-start.js';
import { fetchAndCacheObject } from './object-cache.js';
import { planWithConsent, offerFortunate } from './trigger-consent.js';
import { planMoveTriggers } from './move-trigger-truth.js';
import { commitZoneEntryVerdict, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { movementScopeReaders, actorRowFor, actorEncounterFor, commitResolvedPlainWalk, waitForActorMovement,
  waitForWalkPrefixSave, MovementRefused, type WalkReactionGeometry } from './movement-truth.js';

type Row = Record<string, unknown>;
/** Authored reach events use the same suspended movement plan and controller consent as OA.
 * The shared ability resolver owns gates, charges, damage, status projection and reaction spending. */
export function* planReachWatcherWalk(input: PlainWalkCommitInput, encounter: Row, mover: MovementTruthRow,
  geometry: WalkReactionGeometry): Generator<Promise<unknown>, PlainWalkResult | null, unknown> {
  const contact = geometry.reachReaction!;
  const refuse = (reason: string): never => { throw new MovementRefused(`Movement refused: reach-reaction:${reason}`, `movement:reach-reaction:${reason}`); };
  const read = { ...movementScopeReaders(encounter), ...geometry };
  const watcher = actorRowFor(input.encounterId, contact.watcher);
  if (!watcher) return refuse('watcher-not-held');
  try {
    yield withAsyncDeadline(async () => {
      await hydrateInventoryDefinitions(read.entries(watcher, 'inventory')
        .filter((row): row is Row => !!row && typeof row === 'object' && !Array.isArray(row)), encounter.catalog, fetchAndCacheObject);
      const naturals = await Promise.all(read.entries(watcher, 'natural_weapon').map(entry => {
        const id = readLayerListEntryUuid(entry).toUpperCase();
        return read.definition(id) ?? fetchAndCacheObject(id);
      }));
      await Promise.all([...PC_DYING_STATUS_IDS].filter(id => !read.definition(id)).map(id => fetchAndCacheObject(id)));
      await hydrateTurnTriggerDefinitions(mover, encounter, naturals.filter((row): row is Row => !!row && typeof row === 'object'), [watcher, mover]);
    }, 20_000);
  } catch { return refuse('inputs-unavailable'); }
  const handled = new Set(input.reachReactionsHandled ?? []);
  // Prove the paid route and both outcomes before asking or drawing. A pending authored
  // contact is owned by this interrupted walk; universal OAs retain their own admission.
  const admission = new Set(handled);
  if (contact.trigger === 'triggers_when_target_leaves_reach') admission.add(contact.watcher.toUpperCase());
  for (const member of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [])
    for (const [trigger] of [...WATCHER_REACH_ENTRY_TRIGGERS, ...WATCHER_REACH_LEAVE_TRIGGERS])
      admission.add(reachReactionKey(String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? ''), trigger));
  const scope = resolvePlainWalkScope(input, encounter, mover, read, admission);
  if (!scope.supported) return refuse('route:' + ('reason' in scope ? scope.reason : scope.message));
  const walk = scope.walk;
  // Ordered consequences need a common interruption frame. Keep their exact capability
  // named rather than moving past a trap, companion or zone while resolving a reaction.
  for (const key of ['traversal', 'compound', 'trap', 'overrun', 'climb', 'wallClimb', 'squeeze', 'burrow', 'move_trigger', 'zone_entries', 'zone_exits', 'follower_zone_entries'] as const)
    if (walk[key] && (!Array.isArray(walk[key]) || walk[key]!.length)) return refuse('movement:' + key);
  const feet = legacyStepPositions(walk.route).map(p => [p[0], p[1], walk.origin.z] as [number, number, number]);
  if (contact.step >= feet.length) return refuse('trigger-foot-unpaid');
  const point = feet[contact.step]!;
  const destination = { x: point[0], y: point[1], z: point[2] };
  let prefix: PlainWalkResult | undefined;
  if (contact.step > 0) {
    const prefixScope = resolvePlainWalkScope({ ...input, destination, route: feet.slice(0, contact.step + 1) }, encounter, mover, read, admission);
    if (!prefixScope.supported) return refuse('trigger-foot:' + ('reason' in prefixScope ? prefixScope.reason : prefixScope.message));
    prefix = prefixScope.walk;
  }
  const planned = yield* awaitPlan(planWithConsent(input.encounterId, input.actorId,
    consent => planMoveTriggers(input.encounterId, input.actorId,
      { onMove: false, entered: [], watchers: [{ id: contact.watcher, trigger: contact.trigger }] }, { ...destination,
        aps: prefix?.updates.aps, movement_pct: prefix?.updates.movement_pct }, undefined, consent),
    plan => plan.kind === 'handback' && plan.reason.endsWith('trigger-consent')));
  const plan = planned as ReturnType<typeof planMoveTriggers>;
  if (plan.kind === 'handback') return refuse(plan.reason);
  const expectedRevs = new Map<string, number>(), before = new Map<string, Row>();
  if (plan.kind === 'planned') {
    for (const [id, source] of plan.originals)
      expectedRevs.set(id, Number(encounterCreatureField(source.encounter_creatures, 'movement_rev') ?? 0));
    // A prefix owns only the mover's arrival revision. Preserve the reaction's
    // actual before-values while that save is pending, including other members.
    for (const [id, changes] of Object.entries(plan.verdict.ec)) {
      const source = plan.originals.get(id);
      if (!source) return refuse('participant-not-held:' + id);
      const values = Object.fromEntries(Object.keys(changes).map(key => [key,
        key === 'x_pos' || key === 'y_pos' || key === 'z_pos' ? posCoord(source, key) ?? 0
          : encounterCreatureField(source.encounter_creatures, key)]));
      before.set(id, { encounter_creatures: values, position: values });
    }
  }
  handled.add(reachReactionKey(contact.watcher, contact.trigger));
  if (contact.trigger === 'triggers_when_target_leaves_reach') handled.add(contact.watcher.toUpperCase());
  if (prefix) {
    yield* awaitPlan(commitResolvedPlainWalk({ ...input, destination, route: prefix.route }, mover, prefix));
    yield waitForWalkPrefixSave(waitForActorMovement(input.encounterId, input.actorId));
  }
  if (plan.kind === 'planned') {
    const actor = actorRowFor(input.encounterId, input.actorId);
    if (!actor) return refuse('mover-not-held');
    const revision = Number(encounterCreatureField(actor.encounter_creatures, 'movement_rev') ?? 0);
    expectedRevs.set(input.actorId.toUpperCase(), revision);
    let result: Awaited<ReturnType<typeof commitZoneEntryVerdict>>;
    try {
      result = (yield waitForWalkPrefixSave(withAsyncDeadline(() => commitZoneEntryVerdict({ encounterId: input.encounterId,
        actorId: input.actorId, abilityId: plan.abilityId, zoneStatusId: '', verdict: plan.verdict, before,
        expectedRevs, moveTrigger: { entered: [] } }), 20_000))) as typeof result;
    } catch { return refuse('save-unavailable'); }
    if (result.outcome !== 'served') return refuse('save:' + (result.reason ?? result.outcome));
    if (plan.verdict.fortunateOffers?.length) offerFortunate(input.encounterId, input.actorId, plan.verdict.fortunateOffers);
  }
  const current = actorRowFor(input.encounterId, input.actorId), currentEncounter = actorEncounterFor(input.encounterId);
  if (!current || !currentEncounter) return refuse('mover-not-held');
  const stopped = Number(encounterCreatureField(current.encounter_creatures, 'encounter_creature_dead') ?? 0) === 1
    || ordinaryMovementStartRefusal(current, read, { speed: input.speed ?? 'speed_walking', allowOffTurn: true }) !== null;
  if (stopped || contact.step === feet.length - 1) return prefix ?? { ...walk, destination: { ...input.origin },
    route: [[input.origin.x, input.origin.y, input.origin.z]], speed_cost: 0,
    updates: { x_pos: input.origin.x, y_pos: input.origin.y, z_pos: input.origin.z } };
  const origin = { x: Number(posCoord(current, 'x_pos')), y: Number(posCoord(current, 'y_pos')), z: Number(posCoord(current, 'z_pos') ?? 0) };
  const rest = feet.slice(contact.step); rest[0] = [origin.x, origin.y, origin.z];
  const tail = yield* geometry.resume!({ ...input, origin, destination: walk.destination, route: rest, reachReactionsHandled: [...handled] });
  return tail ? { ...tail, origin: walk.origin, before: walk.before,
    route: [...(prefix?.route.slice(0, -1) ?? []), ...tail.route], speed_cost: (prefix?.speed_cost ?? 0) + tail.speed_cost } : null;
}
